import {Injectable} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {LocalUser, LocalUserAction, LocalUserState} from '@app/state/local-user.state';
import {ClassroomAction, ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {LogService} from '@app/services/log.service';
import {JsonLogAction, LogLevel, LogResource} from '@app/modules/shared/data/constants';
import {InitRoom} from '@app/models/init-room';
import {catchError, map} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {WebSocketService} from '@app/modules/shared/services/web-socket.service';
import {MessageHandleService} from '@app/modules/shared/services/message-handle.service';
import {WebPortalMessagingService} from '@app/services/web-portal-messaging.service';
import {Store} from '@ngxs/store';
import {WrtcService} from '@app/modules/wrtc/services/wrtc.service';
import {CoreService} from '@app/services/core.service';
import {WhiteboardService} from '@app/modules/whiteboard/services/whiteboard.service';
import {BlackboardService} from '@app/modules/blackboard/services/blackboard.service';
import {AssignmentService} from '@app/modules/whiteboard/services/assignment.service';
import {CookieService} from '@app/modules/shared/services/cookie.service';
import {JsonLogService} from '@app/modules/shared/services/json-log.service';
import {VcrEntity} from '@app/models/vcr-entity/entity';
import {User} from '@app/models/vcr-entity/user';
import {ChatService} from '@app/modules/chat/services/chat.service';
import {Device} from '@app/models/device';
import Bowser from 'bowser';
import {MediaConnectionBitrateTest} from '@twilio/rtc-diagnostics';


@Injectable({
  providedIn: 'root'
})
export class ClassroomInitService {

  public isLoading: boolean = true;
  private classroom: ClassroomStateModel;

  private localUser: LocalUser;

  constructor(
    private http: HttpClient,
    private router: Router,
    private store: Store,
    private cookieService: CookieService,
    private coreService: CoreService,
    private jsonLogService: JsonLogService,
    private wrtcService: WrtcService,
    private webSocketService: WebSocketService,
    private messageHandleService: MessageHandleService,
    private webPortalMessagingService: WebPortalMessagingService,
    private chatService: ChatService
  ) {
    this.listenStoreChanges();
  }

  public init(): void {
    this.webSocketService.onConnected$.subscribe((): void => {
      /* listen and handle messages whenever web socket connection is established */
      this.messageHandleService.listen();
    });

    this.getInitInfo().subscribe((roomInitInfo: InitRoom) =>
      this.webSocketService.connect(`/${this.classroom.roomNum}${this.classroom.vcrURL}/ws/websocket?roomNum=${this.classroom.roomNum}`).subscribe((isSocketConnected: boolean) => {
        this.webPortalMessagingService.setIsReady(true);

        if (isSocketConnected && roomInitInfo) {
          if (roomInitInfo.isInDNDMode && !roomInitInfo.isCurrentClassParticipant) {
            let navigateURL: string = `/${this.classroom.roomNum}/classroomClosed`;
            if (roomInitInfo.currentClass) {
              navigateURL += `?classId=${roomInitInfo.currentClass.groupId}`;
            }
            this.router.navigateByUrl(navigateURL).then();
            return;
          }

          /* if there is loaded class */
          if (this.classroom?.currentClass) {
            const joinedClassId: string = this.cookieService.get('joinedClassId');
            if (!joinedClassId || parseInt(joinedClassId) !== this.classroom.currentClass.groupId) {
              this.cookieService.set('joinedClassId', this.classroom.currentClass.groupId + '', 1);
              this.store.dispatch(new LocalUserAction.SetLateTime(roomInitInfo.studentLateTime));
              this.jsonLogService.writeJsonLogs(JsonLogAction.LATE_STUDENT, {selectedClass: this.classroom.currentClass.groupId});
            }
          }

          this.store.dispatch(new ClassroomAction.ToggleConnectedToSocket(true));
          this.store.dispatch(new ClassroomAction.SetServiceUrls(roomInitInfo.cmsUrl, roomInitInfo.studentPortalUrl, roomInitInfo.newStudentPortalUrl));
          this.coreService.initClassmates(roomInitInfo.participants);

          const bowserParser: Bowser.Parser.Parser = Bowser.getParser(window.navigator.userAgent);
          const device: Device = {
            browserName: bowserParser.getBrowserName(),
            browserVersion: bowserParser.getBrowserVersion(),
            osName: bowserParser.getOSName(),
            platform: bowserParser.getPlatformType()
          };
          this.coreService.sendDevice(device).subscribe();

          /* send average bitrate on every room join */
          this.wrtcService.getIceServersUrls().subscribe((response) => {
            const mediaConnectionBitrateTest: MediaConnectionBitrateTest = WrtcService.testMediaConnectionBitrate(response,
              () => {},
              () => {},
              (report: MediaConnectionBitrateTest.Report) => {
                const averageBitrate: number = report.values
                  .filter((bitrate: number): boolean => bitrate > 0)
                  .reduce((total: number, value: number) => total + value, 0) / report.values.length;

                if (averageBitrate > 0) {
                  this.coreService.sendBitrate(averageBitrate).subscribe();
                }
              }
            );
            setTimeout(() => {
              mediaConnectionBitrateTest.stop();
            }, 10000);
          });

          LogService.writeLog('Run passed commands', LogLevel.DEBUG, LogResource.CORE);
          LogService.writeLog('Passed commands applied', LogLevel.DEBUG, LogResource.CORE);

          // connect to WRTC after channel open command is send to make sure that twilio on participant connect event will be called after users receive user data threw socket channel
          this.coreService.sendChannelOpen().subscribe(() => {
            this.coreService.init();
            this.wrtcService.init(roomInitInfo.token, roomInitInfo.roomName);
            this.chatService.sendSystemInfoMessage(this.localUser,'channel open.');
          });

          this.isLoading = false;
        } else {
          LogService.writeLog('Socket connection not established.', LogLevel.ERROR, LogResource.CORE);
          this.store.dispatch(new ClassroomAction.ToggleConnectedToSocket(false));
        }
      })
    );
  }

  private getInitInfo(): Observable<InitRoom> {
    return this.http.get(`${this.classroom.vcrURL}/app/init-info`).pipe(
      map(({data}: any) => {
        const roomInitState: InitRoom = new InitRoom();
        roomInitState.token = data.token;
        roomInitState.roomName = data.roomName;
        roomInitState.studentLateTime = data.studentLateTime;
        roomInitState.currentClass = data.currentClass;
        roomInitState.isCurrentClassParticipant = data.currentClassParticipant;
        roomInitState.isInDNDMode = data.inDNDMode;
        roomInitState.stickers = data.stickers;
        roomInitState.cmsUrl = data.cmsUrl;
        roomInitState.studentPortalUrl = data.studentPortalUrl;
        roomInitState.newStudentPortalUrl = data.newStudentPortalUrl;

        roomInitState.participants = [];
        data.participants?.forEach((participant: any): void => {
          roomInitState.participants.push(VcrEntity.createEntity(User, participant));
        });

        return roomInitState;
      }),
      catchError((err) => {
        if (err.status === 400) {
          this.router.navigateByUrl(`/${this.classroom.roomNum}/classroomClosed`).then();
          return of(null);
        } else {
          this.router.navigateByUrl(`/${this.classroom.roomNum}/login`).then();
          return of(null);
        }
      })
    );
  }

  public navigateToHome(): void {
    this.store.dispatch(new ClassroomAction.ToggleRoomMutedBecauseOfBlockedMicAnCam(false));
    this.isLoading = true;
    this.router.navigateByUrl(`/${this.classroom.roomNum}/`).then();
  }

  private listenStoreChanges(): void {
    this.store.select(ClassroomState).subscribe((classroom: ClassroomStateModel) => {
      this.classroom = classroom;
    });

    this.store.select(LocalUserState).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });
  }
}
